import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { FaCheck, FaPrint } from 'react-icons/fa6';
import { HiMiniDocumentCheck } from 'react-icons/hi2';
import { IoMail } from 'react-icons/io5';
import { LuSend } from 'react-icons/lu';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { MdScoreboard } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Tooltip, ActionIcon } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import ModalForm from '../../../components/forms/ModalForm';
import SendContractEmailForm from '../../../components/forms/SendContractEmailForm';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { ContractRefreshProvider, useContractRefresh } from '../../../contexts/ContractRefreshContext';
import useContractStates from '../../../hooks/api-calls/contract/useContractStates';
import useKoReasons from '../../../hooks/api-calls/useKoReasons';
import useHandleDownloadDocument from '../../../hooks/useDownloadDocument';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/contractProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { ContractProfileLocationState, getAdjacentContractId, getContractNavigationPosition, navigateToContractListReturn, resolveContractListReturnTo } from '../../../utils/contractNavigation';
import { ContractService } from '../../../services/contract/contractService';
import { UserApiResponse } from '../../../type/entities/user-type';
import ScoreModalForm from '../components/ScoreModalForm';
import { BiSolidPhoneCall } from 'react-icons/bi';
import CallModalForm from '../../contracts/components/CallModalForm';
import { PiNetwork } from 'react-icons/pi';
import { FaFileSignature } from 'react-icons/fa';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.contracts.profile.info,
        permission: {
            group: 'contracts',
            action: 'get_contracts',
        },
    },
    {
        label: 'Facturación',
        path: menuRoutes.contracts.profile.invoicing,
        permission: {
            group: 'invoices',
            action: 'list_invoices',
        },
    },
    {
        label: 'Llamadas',
        path: menuRoutes.contracts.profile.calls,
        permission: {
            group: 'contracts',
            action: 'get_contracts',
        },
    },
    {
        label: 'Histórico de estados',
        path: menuRoutes.contracts.profile.statesHistory,
        permission: {
            group: 'contracts',
            action: 'show_contract_status_history',
        },
    },
    {
        label: 'Incidencias',
        path: menuRoutes.contracts.profile.incidences,
        permission: {
            group: 'contracts',
            action: 'list_contract_incidences',
        },
    },
    {
        label: 'Documentos',
        path: menuRoutes.contracts.profile.documents,
        permission: {
            group: 'contracts',
            action: 'list_contracts',
        },
    },
    {
        label: 'Actividad',
        path: menuRoutes.contracts.profile.changelogs,
        permission: {
            group: 'contracts',
            action: 'contract_changes_log',
        },
    },
    {
        label: 'Comentarios',
        path: menuRoutes.contracts.profile.comments,
        permission: {
            group: 'contracts',
            action: 'get_contract_comments',
        },
    },
    {
        label: 'Seguimiento',
        path: menuRoutes.contracts.profile.tracking,
        permission: {
            group: 'contracts',
            action: 'show_contract_tracking',
        },
    },
    {
        label: 'Extras',
        path: menuRoutes.contracts.profile.extras,
        permission: {
            group: 'contracts',
            action: 'show_contract_extras',
        },
    },
    {
        label: 'Histórico de puntuación',
        path: menuRoutes.contracts.profile.scoresHistory,
    },
];

export default function ContractProfileLayout({ children, isLoading }: { children: React.ReactNode, isLoading?: boolean }) {

    // HOOKS

    const { selectedView, listNavigation } = useSelector((state: RootState) => state.contractProfile);
    const { id = '' } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const profileLocationState = (location.state as ContractProfileLocationState | null) ?? null;
    const returnToListPath = resolveContractListReturnTo(profileLocationState, menuRoutes.contracts.path);
    const { userCan } = usePrivilege();
    const dispatch = useDispatch();
    const { handleErrors } = useHandleErrors();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const canChangeStatus = userCan('edit_contracts', 'contracts') && userCan('get_statuses_for_select', 'statuses');
    const { getContractStatesList } = useContractStates({ active: true, action: 'edit', entity: 'contract' });
    const { koReasonsOptions } = useKoReasons();
    const { triggerRefresh } = useContractRefresh();
    const navigate = useNavigate();

    // STATES

    const [openEmailTemplateModal, setOpenEmailTemplateModal] = useState(false);
    const [openScoreModal, setOpenScoreModal] = useState<boolean>(false);
    const [score, setScore] = useState<number>(0);
    const [phone, setPhone] = useState<string>('');
    const [phoneSecondary, setPhoneSecondary] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [openCallModal, setOpenCallModal] = useState<boolean>(false);
    const sourceOrderId = searchParams.get('fromOrderId');
    const sourceOrderView = searchParams.get('fromOrderView');
    const sourceOrderViewPathByKey: Record<string, string> = {
        contracts: menuRoutes.orders.profile.contracts,
        assignments: menuRoutes.orders.profile.assignments,
        exports: menuRoutes.orders.profile.exports,
        campaigns: menuRoutes.orders.profile.campaigns,
    };
    const sourceOrderViewPath = sourceOrderViewPathByKey[sourceOrderView ?? ''] ?? menuRoutes.orders.profile.contracts;
    const backToOrderPath = sourceOrderId
        ? `${menuRoutes.orders.path}/${sourceOrderId}/profile${sourceOrderViewPath}`
        : null;
    const backToOrderLabel = sourceOrderView === 'campaigns' ? 'Volver a campaña' : 'Volver al pedido';
    const backToCommunicationsPath = profileLocationState?.fromCommunications
        ? profileLocationState.returnToCommunicationsPath || `${menuRoutes.calls.path}${menuRoutes.calls.communications}`
        : null;
    const backToTutorIncidencesPath = profileLocationState?.fromTutorIncidences
        ? profileLocationState.returnToTutorIncidencesPath || `${menuRoutes.incidences.path}${menuRoutes.incidences.tutor}`
        : null;

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new ContractService()).getContractById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));

    const pageHeader = {
        title: `Datos del Contrato ${data?.code ? `(${data.code})` : ''}`,
        breadcrumb: [
            { name: 'Ventas' },
            { name: 'Gestión de contratos' },
            { name: 'Contratos', href: returnToListPath },
            { name: 'Detalles del Contrato' },
        ],
    };

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ENVIAR CONTRATO POR SMS
     * @EN SEND CONTRACT BY SMS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleSendContractSms = async (id: string) => {
        try {
            const response = await (await (new ContractService()).sendContractSms(id)).getResponseData();
            if (response.success) {
                toast.success('Comunicación de firma enviada correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al enviar la comunicación de firma');
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO DE CONTRATO
     * @EN CHANGE CONTRACT STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (entityId: string, statusId: string, comment: string, koReasonId?: string) => {
        try {
            const response = await (await (new ContractService()).changeContractStatus(entityId, statusId, comment, koReasonId)).getResponseData();
            if (response.success) {
                toast.success('Estado del contrato actualizado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            refetch();
            // Trigger refresh for ContractStatesHistory component
            triggerRefresh();
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR CONTRATO
     * @EN DELETE CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await (new ContractService()).deleteContract(id)).getResponseData();
            if (response.success) {
                navigateToContractListReturn(navigate, profileLocationState, menuRoutes.contracts.path);
                toast.success('Contrato eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES NAVEGAR A CONTRATO ADYACENTE
     * @EN NAVIGATE TO ADJACENT CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const navigateToAdjacentContract = useCallback((direction: 'prev' | 'next') => {
        if (!listNavigation?.contractIds?.length || !id) {
            return;
        }

        const adjacentContractId = getAdjacentContractId(listNavigation.contractIds, id, direction);
        if (!adjacentContractId) {
            return;
        }

        const returnTo = profileLocationState?.returnTo ?? listNavigation.returnTo;
        navigate(
            `${menuRoutes.contracts.path}/${adjacentContractId}/profile${selectedView}${location.search}`,
            {
                state: {
                    ...profileLocationState,
                    returnTo,
                },
            }
        );
    }, [id, listNavigation, location.search, navigate, profileLocationState, selectedView]);
    

    // EFFECTS

    const allowedPaths = useMemo(() => {
        return menuItems
            .filter((menu: MenuItem) => !menu.permission || userCan(menu.permission.action, menu.permission.group))
            .map((menu: MenuItem) => menu.path);
    }, [userCan]);

    useEffect(() => {
        const rawPath = location.pathname.split('/profile')[1] || '';
        const currentPath = (rawPath || menuRoutes.contracts.profile.info).trim();
        const normalizedPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;

        if (!allowedPaths.includes(normalizedPath)) {
            dispatch(setSelectedView(menuRoutes.contracts.profile.info));
            navigate(`${menuRoutes.contracts.path}/${id}/profile${menuRoutes.contracts.profile.info}${location.search}`, { replace: true, state: location.state });
        } else if (normalizedPath !== selectedView) {
            dispatch(setSelectedView(normalizedPath));
        }
    }, [location.pathname, location.search, location.state, allowedPaths, id, dispatch, navigate, selectedView]);

    const isEnabledFlag = (value: any) => value === true || value === 1 || value === '1' || value === 'true' || value === 't';
    const hasContractSigned = isEnabledFlag(data?.isSigned);
    const hasSignatureCommunication = isEnabledFlag(data?.isSendSmsContractToSign);
    const canSendContractToSignature = isEnabledFlag(data?.canSendContractToSignature);
    const showSignatureResendAction = hasSignatureCommunication && !hasContractSigned;
    const showSignatureSendAction = canSendContractToSignature || showSignatureResendAction;
    const showSignatureActionBlock = hasSignatureCommunication || showSignatureSendAction;
    const signatureSendTooltip = hasSignatureCommunication
        ? 'Reenviar contrato a firma'
        : 'Enviar contrato a firma';

    const contractNavigationPosition = useMemo(() => {
        if (!listNavigation?.contractIds?.length || !id) {
            return null;
        }

        return getContractNavigationPosition(listNavigation.contractIds, id);
    }, [id, listNavigation?.contractIds]);

    // RENDER

    return (
        <>
            <PageHeader
                title={
                    <span className='flex flex-wrap items-center gap-2'>
                        {pageHeader.title}
                        {data?.commercialNetwork?.name && (
                            <Tooltip
                                size="sm"
                                content="Red comercial del contrato"
                                placement="top"
                                color="invert"
                            >
                                <span className='inline-flex items-center gap-1 text-primary text-xs font-medium'>
                                    <PiNetwork className="h-3.5 w-3.5" aria-hidden />
                                    {data.commercialNetwork.name}
                                </span>
                            </Tooltip>
                        )}
                        {data?.isSigned && (
                            <span className='text-green-500 text-xs font-medium'>Contrato firmado</span>
                        )}
                        {data?.isSignedDirectDebitMandate && (
                            <span className='text-green-500 text-xs font-medium'>Autorización domiciliación firmada</span>
                        )}
                    </span>
                }
                breadcrumb={pageHeader.breadcrumb}
            >
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0 align-middle items-center'>
                    {!backToOrderPath && !backToCommunicationsPath && !backToTutorIncidencesPath && contractNavigationPosition && (
                        <div className='flex items-center gap-1'>
                            <Tooltip
                                size="sm"
                                content="Contrato anterior"
                                placement="top"
                                color="invert"
                            >
                                <ActionIcon
                                    as="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={!contractNavigationPosition.hasPrevious}
                                    onClick={() => navigateToAdjacentContract('prev')}
                                    className="hover:!border-gray-900 hover:text-gray-700 disabled:opacity-40"
                                >
                                    <PiCaretLeftBold className="h-4 w-4" />
                                </ActionIcon>
                            </Tooltip>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                {contractNavigationPosition.index + 1} / {contractNavigationPosition.total}
                            </span>
                            <Tooltip
                                size="sm"
                                content="Contrato siguiente"
                                placement="top"
                                color="invert"
                            >
                                <ActionIcon
                                    as="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={!contractNavigationPosition.hasNext}
                                    onClick={() => navigateToAdjacentContract('next')}
                                    className="hover:!border-gray-900 hover:text-gray-700 disabled:opacity-40"
                                >
                                    <PiCaretRightBold className="h-4 w-4" />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}
                    {backToOrderPath ? (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(backToOrderPath)}
                        >
                            <ArrowBack/> {backToOrderLabel}
                        </Button>
                    ) : backToCommunicationsPath ? (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(backToCommunicationsPath)}
                        >
                            <ArrowBack/> Volver a comunicaciones
                        </Button>
                    ) : backToTutorIncidencesPath ? (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(backToTutorIncidencesPath)}
                        >
                            <ArrowBack/> Volver a incidencias de tutor
                        </Button>
                    ) : (
                        <Button
                            variant={'outline'}
                            size={'sm'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigateToContractListReturn(navigate, profileLocationState, menuRoutes.contracts.path)}
                        >
                            <ArrowBack/> Volver
                        </Button>
                    )}
                    {showSignatureActionBlock && (
                        <div className='flex justify-center h-min me-2'>
                            {hasContractSigned && !showSignatureSendAction ? (
                                /* --- ESTADO: YA FIRMADO --- */
                                <Tooltip
                                    size="sm"
                                    content={'Contrato firmado correctamente'}
                                    placement="top"
                                    color="success"
                                >
                                    <div className='flex items-center text-success-600 font-medium gap-1'>
                                        <FaFileSignature className="h-5 w-5" size={20} color='blue' />
                                        <span className="text-xs">Firmado</span>
                                    </div>
                                </Tooltip>
                            ) : (
                                /* --- ESTADO: PENDIENTE DE FIRMA --- */
                                <>
                                    {hasSignatureCommunication && (
                                        <Tooltip
                                            size="sm"
                                            content={'Contrato enviado por SMS/email para su firma'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <div className='h-min'>
                                                <FaCheck className="h-4 w-4" size={20} color='green' />
                                            </div>
                                        </Tooltip>
                                    )}
                                    {showSignatureSendAction && (
                                        <Tooltip
                                            size="sm"
                                            content={signatureSendTooltip}
                                            placement="top"
                                            color="invert"
                                        >
                                            <div className='h-min'>
                                                <LuSend
                                                    className={`${hasSignatureCommunication ? 'ms-4 ' : ''}h-4 w-4 cursor-pointer hover:text-blue-500 transition-colors`}
                                                    size={20}
                                                    onClick={() => handleSendContractSms(data?.id)}
                                                />
                                            </div>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {data && (
                        <div className='flex flex-col gap-2 justify-content-center text-center' key={data?.currentContractStatus?.id}>
                            <StatusDropdown
                                badgeSize='sm'
                                disabled={!canChangeStatus}
                                entityId={data?.id}
                                title="¿Estás seguro de cambiar el estado del contrato?"
                                statesOptions={getContractStatesList(null, data?.currentContractStatus?.id).filter((state: any) => !state.productTypes?.length || state.productTypes?.includes(data?.productType?.id))}
                                currentState={data?.currentContractStatus}
                                currentStateDate={data?.currentContractStatusDate?.date}
                                koReasonsOptions={koReasonsOptions}
                                koReasonStatusEntity="contract"
                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) => handleStateChange(entityId, statusId, comment, koReasonId)}
                            />
                        </div>
                    )}

                    {data && userCan('realize_calls', 'calls') && (
                        <>
                            {(() => {
                                const hasPhone = Boolean(data.clientTelephone && data.clientTelephone.trim() !== "");
                                const hasPhoneSecondary = Boolean(data.clientTelephoneSecondary && data.clientTelephoneSecondary.trim() !== "");
                                const hasMobile = Boolean(data.clientMobile && data.clientMobile.trim() !== "");
                                const hasAnyPhone = hasPhone || hasPhoneSecondary || hasMobile;

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={
                                            hasAnyPhone
                                                ? 'Realizar llamada'
                                                : 'El cliente no posee ningún número de teléfono'
                                        }
                                        placement="top"
                                        color={hasAnyPhone ? "invert" : "danger"}
                                    >
                                        <Button
                                            variant="outline"
                                            disabled={!hasAnyPhone}
                                            className={
                                                hasAnyPhone
                                                    ? "hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                    : "opacity-50 cursor-not-allowed"
                                            }
                                            onClick={() => {
                                                if (!hasAnyPhone) return;
                                                setPhone(data.clientTelephone);
                                                setPhoneSecondary(data.clientTelephoneSecondary);
                                                setMobile(data.clientMobile);
                                                setOpenCallModal(true);
                                            }}
                                        >
                                            <BiSolidPhoneCall className="h-4 w-4" size={20} />
                                        </Button>
                                    </Tooltip>
                                );
                            })()}
                            <div className='border-l border-gray-300' />
                        </>
                    )}

                    {userCan('update_contracts_score', 'contracts') && (
                        <Tooltip
                            size="sm"
                            content={'Modificar puntuación'}
                            placement="top"
                            color="invert"
                        >
                            <Button
                                variant="outline"
                                size='md'
                                onClick={() => { setScore(data?.score || 0); setOpenScoreModal(true); }}
                            >
                                <MdScoreboard className="h-4 w-4" size={20} />
                            </Button>
                        </Tooltip>
                    )}

                    {data && userCan('manage_company_email_templates', 'user') && (
                        <Tooltip
                            size="sm"
                            content={'Enviar correo electrónico'}
                            placement="top"
                            color="invert"
                        >
                            <Button
                                variant="outline"
                                size='md'
                                onClick={() => { setOpenEmailTemplateModal(true) }}
                            >
                                <IoMail className="h-4 w-4" size={20} />
                            </Button>
                        </Tooltip>
                    )}

                    {userCan('print_contract', 'contracts') && data?.isDraft === false && (
                        <Tooltip content={data?.isSigned ? 'Descargar PDF del contrato firmado' : 'Descargar PDF del contrato'} placement={'top'} color={'invert'} size={'md'}>
                            <Button
                                variant={'outline'}
                                size={'md'}
                                onClick={() => handleDownloadDocumentNewTab((new ContractService()).generateContractPdf, data?.id, moment().format('DD-MM-YYYY') + '_contrato.pdf')}
                            >
                                {data?.isSigned
                                    ? <HiMiniDocumentCheck className="h-4 w-4" size={20} />
                                    : <FaPrint className="h-4 w-4" size={20} />
                                }
                            </Button>
                        </Tooltip>
                    )}

                    {userCan('edit_contracts', 'contracts') && (
                        <Tooltip content={'Editar contrato'} placement={'top'} color={'invert'} size={'md'}>
                            <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                navigate(`${menuRoutes.contracts.path}/${id}/edit`)
                            }}>
                                Editar
                            </Button>
                        </Tooltip>
                    )}

                    {userCan('delete_contracts', 'contracts') && (
                        <Tooltip content={'Eliminar contrato'} placement={'top'} color={'invert'} size={'md'} className='text-danger'>
                            <div>
                                <DeletePopover
                                    title={`Eliminar contrato`}
                                    description={`¿Estás seguro de que deseas eliminar el contrato?`}
                                    size={'md'}
                                    onDelete={() => handleDelete(id)}
                                    actionIconClassName='h-auto w-10 h-10'
                                />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading && !data ? <Loader height='60vh' /> : children}

            <ScoreModalForm isOpen={openScoreModal} onClose={() => setOpenScoreModal(false)} refetch={refetch} id={id} score={score} setScore={setScore} />

            <CallModalForm
                isOpen={openCallModal}
                onClose={() => setOpenCallModal(false)}
                refetch={refetch}
                contractId={id}
                currentClientId={data?.client?.id}
                phone={phone}
                phoneSecondary={phoneSecondary}
                mobile={mobile}
            />

            <ModalForm size='xl' isOpen={openEmailTemplateModal} onClose={() => setOpenEmailTemplateModal(false)} title='Enviar correo electrónico del contrato'>
                <SendContractEmailForm onClose={() => setOpenEmailTemplateModal(false)} contractId={data?.id || ''} clientEmail={data?.clientEmail || ''} />
            </ModalForm>
        </>
    );
}
